'use strict';

const { getSelectedSupplier } = require('public/js/lib/supplier-state');
const { SUPPLIER_RESOLVED_EVENT } = require('public/js/lib/supplier-events');
const { waitForSupplierResolution } = require('public/js/lib/supplier-ready');

// Exclude product_paypal_messaging, which reuses the class without catalog/sku attrs.
const MODULE_SELECTOR = '.product-purchase-module[data-catalog]';
const HTML_ENDPOINT = '/ajax/product/get-product-purchase-html';

/** @returns {string|null} SSR fulfillment hash, or null when the page has no supplier-priced module. */
function getRenderedFulfillmentHash() {
	const $module = rp$(MODULE_SELECTOR);
	if ($module.length === 0) {
		return null;
	}

	const renderedHash = $module.attr('data-ssr-fulfillment-hash');

	return typeof renderedHash === 'string' ? renderedHash : null;
}

/**
 * PDP encoded context (`c`) carries selected_variant_pid / selected_option_values.
 *
 * @returns {string}
 */
function getProductUrlContextParam() {
	const encodedContext = new URLSearchParams(globalThis.location.search).get('c');

	return typeof encodedContext === 'string' ? encodedContext : '';
}

/**
 * @returns {Promise<{fulfillmentHash: string, html: string, modules?: Record<string, string>}|null>}
 */
function fetchPurchaseModule() {
	const $module = rp$(MODULE_SELECTOR);
	const catalog = $module.data('catalog') || '';
	const sku = $module.data('sku-stripped') || '';

	if (catalog === '' || sku === '') {
		return Promise.resolve(null);
	}

	const data = { catalog, sku };
	const encodedContext = getProductUrlContextParam();
	if (encodedContext !== '') {
		data.c = encodedContext;
	}

	return new Promise((resolve) => {
		$ajax({
			method: 'GET',
			url: HTML_ENDPOINT,
			dataType: 'json',
			data,
			success: (response) => {
				const html = response?.html;
				const modulesById = response?.modules;
				const hasModuleMap = modulesById !== null && typeof modulesById === 'object';
				const hasSingleModuleHtml = typeof html === 'string' && html !== '';
				const hasMappedModuleHtml = hasModuleMap
					&& Object.values(modulesById).some((moduleHtml) => typeof moduleHtml === 'string' && moduleHtml !== '');

				if (! hasSingleModuleHtml && ! hasMappedModuleHtml) {
					resolve(null);
					return;
				}

				resolve({
					fulfillmentHash: String(response.fulfillmentHash ?? ''),
					html: hasSingleModuleHtml ? html : '',
					modules: hasModuleMap ? modulesById : undefined,
				});
			},
			// Prefer stale pricing over blanking the purchase box.
			error: () => resolve(null),
		});
	});
}

/**
 * @param {{fulfillmentHash?: string, html?: string, modules?: Record<string, string>}} response
 */
function replacePurchaseModules(response) {
	const modulesById = response?.modules;
	const hasModuleMap = modulesById !== null && typeof modulesById === 'object';

	if (hasModuleMap) {
		rp$(MODULE_SELECTOR).each(function replaceMatchingModule() {
			const moduleId = rp$(this).attr('data-module-id');
			const html = typeof moduleId === 'string' ? modulesById[moduleId] : undefined;

			if (typeof html === 'string' && html !== '') {
				rp$(this).replaceWith(html);
			}
		});

		return;
	}

	const html = response?.html;
	if (typeof html === 'string' && html !== '') {
		rp$(MODULE_SELECTOR).replaceWith(html);
	}
}

/** @type {Promise<boolean>|null} */
let refreshInFlight = null;
/** @type {string|null} */
let refreshInFlightHash = null;

/**
 * Swaps purchase markup when the resolved supplier differs from the SSR hash.
 *
 * @param {string} supplierHash
 * @param {() => void} reinitialize
 * @returns {Promise<boolean>}
 */
function refreshPurchaseModuleForSupplier(supplierHash, reinitialize) {
	const renderedHash = getRenderedFulfillmentHash();
	if (renderedHash === null || renderedHash === supplierHash) {
		return Promise.resolve(false);
	}

	// Collapse duplicate resolves while the same supplier refresh is already in flight.
	if (refreshInFlight !== null && refreshInFlightHash === supplierHash) {
		return refreshInFlight;
	}

	refreshInFlightHash = supplierHash;
	refreshInFlight = fetchPurchaseModule().then((response) => {
		// A newer refresh for a different supplier superseded this flight.
		if (refreshInFlightHash !== supplierHash) {
			return false;
		}

		// Reject empty/mismatched hashes (no-supplier offer list, sole-offer, or stale session).
		if (response?.fulfillmentHash !== supplierHash) {
			return false;
		}

		replacePurchaseModules(response);
		reinitialize();

		return true;
	}).finally(() => {
		if (refreshInFlightHash === supplierHash) {
			refreshInFlight = null;
			refreshInFlightHash = null;
		}
	});

	return refreshInFlight;
}

/** @param {() => void} reinitialize */
function bindSupplierPurchaseRefresh(reinitialize) {
	let heardBroadcast = false;

	rp$(document).on(SUPPLIER_RESOLVED_EVENT, function handleSupplierResolved(_event, payload) {
		const supplierHash = payload?.supplier?.hash;
		if (typeof supplierHash !== 'string' || supplierHash === '') {
			return;
		}

		heardBroadcast = true;
		refreshPurchaseModuleForSupplier(supplierHash, reinitialize);
	});

	// Catch up if resolution settled before this module bound; skip if the broadcast already ran.
	waitForSupplierResolution().then(() => {
		const supplier = getSelectedSupplier();
		if (heardBroadcast || supplier === null) {
			return;
		}

		refreshPurchaseModuleForSupplier(supplier.hash, reinitialize);
	});
}

module.exports = {
	bindSupplierPurchaseRefresh,
	refreshPurchaseModuleForSupplier,
};
